import type { DragEndEvent, DragMoveEvent } from "@dnd-kit/core";
import type { RefObject } from "react";
import { useCallback } from "react";
import { navigationTreeStore } from "../store/navigationTreeStore";
import { parseBeforeItemSlotId } from "../utils/beforeItemSlot";
import { DropMode, isGroupSlotMode } from "../utils/dropMode";
import { getDropMode } from "../utils/getDropMode";
import { groupIdFromSlotMode, lastSlotId, parseGroupSlotId } from "../utils/groupSlotId";
import { isDescendant } from "../utils/isDescendant";
import { useNavigationDndBase } from "./useNavigationDndBase";

export const useNavigationDnd = (containerRef: RefObject<HTMLDivElement | null>) => {
	const {
		sensors,
		onDragStart,
		onDragCancel,
		finishDrag,
		setDragTarget,
		startAutoScroll,
		cancelAutoExpand,
		scheduleAutoExpand,
		trackPointer,
	} = useNavigationDndBase(containerRef);

	const onDragMove = useCallback(
		({ active, activatorEvent, delta, over }: DragMoveEvent) => {
			const { childrenMap, rootIds, parentMap, expanded } = navigationTreeStore.getState();
			const pointerY = trackPointer({ activatorEvent, delta });
			startAutoScroll();
			const draggedId = String(active.id);

			if (!over) {
				const isBelowContainer = pointerY >= (containerRef.current?.getBoundingClientRect().bottom ?? Infinity);

				if (isBelowContainer) {
					const lastGroupId = rootIds[rootIds.length - 1];
					if (lastGroupId) {
						const invalid = draggedId === lastGroupId || isDescendant(childrenMap, draggedId, lastGroupId);
						setDragTarget(
							invalid
								? null
								: { anchorId: lastSlotId(lastGroupId), parentId: null, mode: DropMode.LastRoot },
						);
						cancelAutoExpand();
						return;
					}
				}

				setDragTarget(null);
				cancelAutoExpand();
				return;
			}

			const overId = String(over.id);
			const beforeItemId = parseBeforeItemSlotId(overId);
			if (beforeItemId) {
				const invalid = draggedId === beforeItemId || isDescendant(childrenMap, draggedId, beforeItemId);
				cancelAutoExpand();
				setDragTarget(
					invalid
						? null
						: { anchorId: beforeItemId, parentId: parentMap[beforeItemId] ?? null, mode: DropMode.Before },
				);
				return;
			}

			const slot = parseGroupSlotId(overId);
			if (slot) {
				const { groupId, mode } = slot;
				const invalid = draggedId === groupId || isDescendant(childrenMap, draggedId, groupId);
				cancelAutoExpand();
				setDragTarget(invalid ? null : { anchorId: overId, parentId: groupId, mode });
				return;
			}

			if (overId === draggedId || isDescendant(childrenMap, draggedId, overId)) {
				setDragTarget(null);
				cancelAutoExpand();
				return;
			}

			const rect = over?.rect;
			if (!rect) return;

			// Top strip `before`, middle `into`, bottom strip `after` — the same reading on every row. The one
			// exception is an open container: its row ends right above its own children, so an `after` line there
			// would be drawn between the row and its content while meaning "after the whole subtree". That strip
			// reads as `into` instead, which draws nothing there. Dropping after the subtree stays reachable from
			// the top strip of the row that follows it.
			const hasChildren = (childrenMap[overId]?.length ?? 0) > 0;
			const isOpenContainer = hasChildren && expanded.has(overId);
			const pointerMode = getDropMode(pointerY, rect);
			const mode = pointerMode === DropMode.After && isOpenContainer ? DropMode.Into : pointerMode;

			setDragTarget({
				anchorId: overId,
				parentId: mode === DropMode.Into ? null : (parentMap[overId] ?? null),
				mode,
			});

			if (mode === DropMode.Into && hasChildren && !isOpenContainer) scheduleAutoExpand(overId);
			else cancelAutoExpand();
		},
		[containerRef, setDragTarget, cancelAutoExpand, scheduleAutoExpand, startAutoScroll, trackPointer],
	);

	const onDragEnd = useCallback(
		({ active }: DragEndEvent) => {
			const { dragTarget, onDrop, childrenMap: currentChildren } = navigationTreeStore.getState();
			if (dragTarget && onDrop) {
				const draggedId = String(active.id);
				const { anchorId, mode } = dragTarget;

				if (mode === DropMode.LastRoot) {
					const slot = parseGroupSlotId(anchorId);
					if (slot) void onDrop(draggedId, slot.groupId, DropMode.After);
				} else if (isGroupSlotMode(mode)) {
					const groupId = groupIdFromSlotMode(anchorId, mode);
					const lastChild = (currentChildren[groupId] ?? []).filter((id) => id !== draggedId).at(-1);

					if (mode === DropMode.LastChild && lastChild) void onDrop(draggedId, lastChild, DropMode.After);
					else void onDrop(draggedId, groupId, DropMode.Into);
				} else {
					void onDrop(draggedId, anchorId, mode);
				}
			}
			finishDrag();
		},
		[finishDrag],
	);

	return { sensors, onDragStart, onDragMove, onDragEnd, onDragCancel };
};
